import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Target, Rocket, Trophy, Zap, Loader2, Trash2, ChevronDown, ChevronUp, ArrowLeft, Download, CheckSquare, Square } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEVELS = [
  {
    id: "rookie",
    name: "Rookie",
    subtitle: "Estoy empezando desde cero",
    icon: Target,
    color: "emerald",
    bgClass: "bg-emerald-500/10 border-emerald-500/30 hover:border-emerald-500/60",
    iconClass: "text-emerald-400",
    selectedClass: "bg-emerald-500/20 border-emerald-500",
  },
  {
    id: "intermediate",
    name: "Intermedio",
    subtitle: "Ya tengo contactos y algo de experiencia",
    icon: Rocket,
    color: "amber",
    bgClass: "bg-amber-500/10 border-amber-500/30 hover:border-amber-500/60",
    iconClass: "text-amber-400",
    selectedClass: "bg-amber-500/20 border-amber-500",
  },
  {
    id: "pro",
    name: "Pro",
    subtitle: "Ya opero pero quiero escalar",
    icon: Trophy,
    color: "purple",
    bgClass: "bg-purple-500/10 border-purple-500/30 hover:border-purple-500/60",
    iconClass: "text-purple-400",
    selectedClass: "bg-purple-500/20 border-purple-500",
  },
];

function PlanCard({ plan, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(plan.completed_tasks || []);
  const level = LEVELS.find((l) => l.id === plan.level) || LEVELS[0];
  const Icon = level.icon;

  // Extract task lines from content for counting
  const taskLines = (plan.content || "").split("\n").filter(l => l.trim().match(/^-\s*\[/));
  const totalTasks = Math.max(taskLines.length, 1);
  const progress = Math.round((completedTasks.length / totalTasks) * 100);

  const toggleTask = async (index) => {
    try {
      const res = await axios.post(`${API}/plans/${plan.id}/toggle-task`, { task_index: index });
      setCompletedTasks(res.data.completed_tasks);
    } catch (e) {
      console.error("Error toggling task", e);
    }
  };

  const handleExportPdf = async (e) => {
    e.stopPropagation();
    try {
      const res = await axios.get(`${API}/export/plan/${plan.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan_${plan.id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Error exporting PDF", e);
    }
  };

  // Custom renderer that replaces markdown checkboxes with interactive ones
  let taskIndex = -1;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl overflow-hidden"
    >
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-900/60 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${level.id === "rookie" ? "bg-emerald-500/10" : level.id === "intermediate" ? "bg-amber-500/10" : "bg-purple-500/10"}`}>
            <Icon size={16} className={level.iconClass} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-zinc-200">Plan {level.name}</p>
              {completedTasks.length > 0 && (
                <span className="text-[9px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full font-medium">
                  {progress}%
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 truncate max-w-[250px]">{plan.goal}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-600">{plan.created_at?.slice(0, 10)}</span>
          <button
            data-testid={`export-plan-${plan.id}`}
            onClick={handleExportPdf}
            className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-600 hover:text-emerald-400 transition-all"
            title="Exportar PDF"
          >
            <Download size={12} />
          </button>
          <button
            data-testid={`delete-plan-${plan.id}`}
            onClick={(e) => { e.stopPropagation(); onDelete(plan.id); }}
            className="p-1.5 rounded-lg hover:bg-red-500/10 text-zinc-600 hover:text-red-400 transition-all"
          >
            <Trash2 size={12} />
          </button>
          {expanded ? <ChevronUp size={14} className="text-zinc-500" /> : <ChevronDown size={14} className="text-zinc-500" />}
        </div>
      </div>

      {/* Progress bar */}
      {completedTasks.length > 0 && (
        <div className="px-4 pb-0">
          <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-zinc-800/30 pt-4">
              <div className="markdown-content text-sm leading-relaxed text-zinc-300">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                  h1: ({ children }) => <h1 className="text-lg font-bold text-zinc-50 mb-3 mt-1">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-bold text-zinc-100 mb-2 mt-4">{children}</h2>,
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  strong: ({ children }) => <strong className="text-zinc-50 font-semibold">{children}</strong>,
                  ul: ({ children }) => <ul className="pl-1 mb-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                  li: ({ children }) => {
                    const text = String(children);
                    const isTask = text.includes("[ ]") || text.includes("[x]");
                    if (isTask) {
                      taskIndex++;
                      const idx = taskIndex;
                      const done = completedTasks.includes(idx);
                      return (
                        <li
                          className={`flex items-start gap-2 cursor-pointer group py-0.5 ${done ? "opacity-60" : ""}`}
                          onClick={() => toggleTask(idx)}
                          data-testid={`task-${plan.id}-${idx}`}
                        >
                          {done ? (
                            <CheckSquare size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <Square size={16} className="text-zinc-600 group-hover:text-zinc-400 flex-shrink-0 mt-0.5" />
                          )}
                          <span className={done ? "line-through text-zinc-600" : "text-zinc-300"}>
                            {text.replace(/\[[ x]\]\s*/, "")}
                          </span>
                        </li>
                      );
                    }
                    return <li className="text-zinc-300 ml-3 list-disc">{children}</li>;
                  },
                  blockquote: ({ children }) => <blockquote className="border-l-2 border-emerald-500/50 pl-3 my-3 text-zinc-400 italic">{children}</blockquote>,
                  hr: () => <hr className="border-zinc-800 my-4" />,
                  em: ({ children }) => <em className="text-zinc-400 italic">{children}</em>,
                }}>{plan.content}</ReactMarkdown>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function PlansView() {
  const [plans, setPlans] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [goal, setGoal] = useState("");

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plans`);
      setPlans(res.data);
    } catch (e) {
      console.error("Error fetching plans", e);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleGenerate = async () => {
    if (!selectedLevel || !goal.trim() || generating) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/plans/generate`, { level: selectedLevel, goal: goal.trim() });
      setPlans((prev) => [res.data, ...prev]);
      setShowForm(false);
      setSelectedLevel(null);
      setGoal("");
    } catch (e) {
      console.error("Error generating plan", e);
    }
    setGenerating(false);
  };

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/plans/${planId}`);
      setPlans((prev) => prev.filter((p) => p.id !== planId));
    } catch (e) {
      console.error("Error deleting plan", e);
    }
  };

  if (showForm) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-900/80 flex-shrink-0">
          <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-zinc-50" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Nuevo Plan de Accion
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">Edgar IA creara tu plan personalizado de 30 dias</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-8">
            {/* Level Selection */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-300 mb-4 tracking-wide uppercase" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                1. Selecciona tu nivel
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {LEVELS.map((level) => {
                  const Icon = level.icon;
                  const isSelected = selectedLevel === level.id;
                  return (
                    <button
                      key={level.id}
                      data-testid={`level-${level.id}`}
                      onClick={() => setSelectedLevel(level.id)}
                      className={`flex flex-col items-center gap-2.5 p-5 rounded-2xl border-2 transition-all text-center ${
                        isSelected ? level.selectedClass : level.bgClass
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        level.id === "rookie" ? "bg-emerald-500/15" : level.id === "intermediate" ? "bg-amber-500/15" : "bg-purple-500/15"
                      }`}>
                        <Icon size={22} className={level.iconClass} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-zinc-100">{level.name}</p>
                        <p className="text-[11px] text-zinc-500 mt-0.5">{level.subtitle}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Goal Input */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-300 mb-3 tracking-wide uppercase" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                2. Tu objetivo principal
              </h3>
              <textarea
                data-testid="plan-goal-input"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Ej: Quiero conseguir mi primer jugador representado en los proximos 30 dias..."
                rows={3}
                className="w-full bg-zinc-950/60 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-zinc-200 placeholder-zinc-600 outline-none focus:border-zinc-600 transition-colors resize-none"
              />
            </div>

            {/* Generate Button */}
            <button
              data-testid="generate-plan-btn"
              onClick={handleGenerate}
              disabled={!selectedLevel || !goal.trim() || generating}
              className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full py-3 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_24px_rgba(16,185,129,0.25)]"
            >
              {generating ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Edgar IA esta creando tu plan...
                </>
              ) : (
                <>
                  <Zap size={16} />
                  Generar Plan de 30 Dias
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-900/80 flex-shrink-0">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-zinc-50" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Planes de Accion
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">Planes personalizados de 30 dias por Edgar IA</p>
        </div>
        <button
          data-testid="new-plan-btn"
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-5 py-2.5 text-sm transition-all hover:bg-emerald-400 hover:scale-[1.02] active:scale-[0.98] shadow-[0_0_20px_rgba(16,185,129,0.2)]"
        >
          <Zap size={14} />
          Nuevo Plan
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto space-y-3">
          {plans.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="w-16 h-16 mx-auto rounded-2xl bg-zinc-900/60 flex items-center justify-center mb-4">
                <Target size={28} className="text-zinc-600" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-300 mb-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Sin planes aun
              </h3>
              <p className="text-xs text-zinc-500 mb-6 max-w-xs mx-auto leading-relaxed">
                Crea tu primer plan de accion personalizado de 30 dias.
                Edgar IA te dara tareas concretas semana a semana segun tu nivel.
              </p>
              <button
                data-testid="empty-new-plan-btn"
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 bg-emerald-500 text-zinc-950 font-semibold rounded-full px-6 py-2.5 text-sm transition-all hover:bg-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.2)]"
              >
                <Zap size={14} />
                Crear mi primer plan
              </button>
            </motion.div>
          ) : (
            <AnimatePresence>
              {plans.map((plan) => (
                <PlanCard key={plan.id} plan={plan} onDelete={handleDelete} />
              ))}
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>
  );
}
